//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import {
  CampaignRequestProductSource,
  CreatorCampaignRequestProductVariantDimension,
} from '@apis/campaign-requests/creators/CreatorsCampaignRequestApiModel';
import {
  AffiliateType,
  CreatorShippingDetails,
  CreatorsCampaignOrderCreationStatus,
} from '@apis/creators-campaigns/CreatorsCampaignsApiModel';
import { SocialNetwork } from '@models/SocialNetwork';

export interface FixedPay {
  comment?: string;
  currency?: string;
  proposeCost?: number;
  value?: number;
}

export interface CreatorNotes {
  text?: string;
}

export interface ContentsRights {
  description?: string;
  expiryDate?: number;
}

export interface Product {
  description?: string;
  displayProductId?: string; // the id that either comes from external stores like shopify or a manually added id by the client
  externalVariantId?: string; // the variant id that either comes from external stores like shopify
  image?: string; // image here refers to url of product image
  name?: string;
  orderEligibility?: CampaignProposalProductOrderEligibility;
  pdpUrl?: string;
  price?: number;
  quantity?: number;
  source?: CampaignRequestProductSource;
  sourceProductId?: string;
  storeType?: CreatorCampaignProductStoreType;
  variantDimensions?: CreatorCampaignRequestProductVariantDimension[];
  variantId?: string; // style id of the variant
  variantItemId?: string; // item id of the variant
}

export enum CampaignProposalProductOrderEligibility {
  ELIGIBLE = 'eligible',
  INELIGIBLE = 'ineligible',
  ORDERED = 'ordered',
}

export enum CreatorCampaignProductStoreType {
  CUSTOM = 'CUSTOM',
  MAGENTO = 'MAGENTO',
  SHOPIFY = 'SHOPIFY',
}

export interface ProductsConfig {
  numberOfProducts?: number;
  maxNumberOfProducts?: number;
  maxCostOfProducts?: number;
}

export interface Products {
  config?: ProductsConfig;
  selectedProducts?: Product[];
}

export interface EventsTimestamp {
  proposalSubmittedAt?: Date;
  proposalAcceptedAt?: Date;
  proposalRejectedAt?: Date;
}

export interface InstagramTasks {
  post?: InstagramTask;
  reel?: InstagramTask;
  story?: InstagramTask;
}

export interface TikTokTasks {
  brandName?: string;
  videos?: Task[];
  sparkAdsDuration?: string;
}

export interface YouTubeTasks {
  videos?: Task[];
  shorts?: Task[];
}

export interface Task {
  title?: string;
  guidance?: string;
}

export interface InstagramTask extends Task {
  shareAdCode?: boolean;
}

export interface Tasks {
  instagramTasks?: InstagramTasks;
  tiktokTasks?: TikTokTasks;
  youtubeTasks?: YouTubeTasks;
}

export interface ProposalDetailsShippingInfo extends CreatorShippingDetails {
  company?: string;
}

export interface CreatorsCampaignsProposalDetailsRequest {
  campaignSlugName: string;
  creatorId: string;
}

export interface CreatorsCampaignsProposalDetailsResponseBase {
  shippingAddress?: ProposalDetailsShippingInfo;
  status?: string; // TODO: PD-264988 Change proposalStatus from a string type to an enum type
  fixedPay?: FixedPay;
  contentsRights?: ContentsRights;
  creatorNotes?: CreatorNotes;
  products?: Products;
  tasks?: Tasks;
  googleAnalyticsConfiguration?: ProposalDetailsGoogleAnalyticsConfiguration;
  affiliateCode?: AffiliateCode;
  influencersCommission?: InfluencersCommission;
  numberOfOrderEligibleProducts?: number;
  orderCreationStatus?: CreatorsCampaignOrderCreationStatus;
  orderIds?: string[];
}

export interface CreatorsCampaignsProposalOrderEligibleProductsResponse {
  orderEligibleProducts?: Product[];
  totalNumberOfSelectedProducts?: number;
}

export interface ProposalDetailsGoogleAnalyticsConfiguration {
  enabled?: boolean;
  destinationUrl?: string;
}

export interface AffiliateCode {
  code?: string;
  url?: string;
}

export interface InfluencersCommission {
  collectPaymentDetails?: boolean;
  commissionType?: CreatorsCampaignInfluencerCommissionType;
  commissionValue?: number;
  currency?: string;
}

export enum CreatorsCampaignInfluencerCommissionType {
  PERCENTAGE = 'PERCENTAGE',
  FIXED = 'FIXED',
}

export interface CreatorsCampaignsProposalDetailsResponse
  extends CreatorsCampaignsProposalDetailsResponseBase {
  eventsTimestamp?: EventsTimestamp;
}

export interface CreatorsCampaignsProposalStatusRequestBase {
  isFromIntegratedEmail?: boolean;
  senderEmail?: string;
  proposalStatus?: 'approved' | 'rejected' | 'submitted';
  proposalRemarkByBrand?: string;
  creator?: Creator;
  affiliateDetails?: AffiliateDetails;
}

export interface Creator {
  username?: string;
  firstName?: string;
  lastName?: string;
  network?: SocialNetwork;
}

export interface AffiliateDetails {
  affiliateType?: AffiliateType;
  url?: string;
}

export interface CreatorsCampaignsProposalStatusRequest
  extends CreatorsCampaignsProposalDetailsRequest {
  requestBody: CreatorsCampaignsProposalStatusRequestBase;
}
